/**
 * NAOKO AI Recommendations Widget
 * Vanilla JS — zero external dependencies — loaded with defer.
 *
 * Handles layers 2 and 3 of the latency architecture:
 *   Layer 2 (~50ms)  — sessionStorage cache: instant swap on repeat PDP views
 *   Layer 3 (~860ms) — API fetch to /recommend on cache miss
 *
 * Layer 1 (0ms) is handled server-side in snippets/ai-recs-widget.liquid
 * via the ai_recs.recommendations metafield.
 *
 * CLS = 0: swapWidget() replaces ONLY textContent/src/href inside
 * existing data-ai-* elements — never innerHTML on the wrapper.
 *
 * FOUC prevention: shimmer → personalized is perceived as natural loading.
 * Errors degrade silently — widget stays in its current state (shimmer or ready).
 */

(function () {
  'use strict';

  // ── Season helper ──────────────────────────────────────────────────────

  function getSeason() {
    var m = new Date().getMonth() + 1;
    if (m >= 3 && m <= 5)  return 'wiosna';
    if (m >= 6 && m <= 8)  return 'lato';
    if (m >= 9 && m <= 11) return 'jesien';
    return 'zima';
  }

  // ── Customer context — injected by theme.liquid ────────────────────────
  // window.__naokoCustomerId: Shopify customer.id (number) or null
  // window.__naokoOrderCount: customer.orders_count (number, 0 for guests)

  function getCustomerId() {
    return window.__naokoCustomerId || null;
  }

  function getOrderCount() {
    return window.__naokoOrderCount || 0;
  }

  // ── API URL — priority: section data attr > window global > localhost ──

  function getApiUrl(widgetEl) {
    var sectionEl = widgetEl.closest('[data-ai-api-url]');
    return (sectionEl && sectionEl.dataset.aiApiUrl)
      || window.__naokoApiUrl
      || 'http://localhost:3000';
  }

  // ── Language / country from Shopify globals ────────────────────────────

  function getLanguage() {
    var lang = document.documentElement.lang;
    return (lang && lang.split('-')[0].toLowerCase()) || 'pl';
  }

  function getCountry() {
    return (window.Shopify && window.Shopify.country)
      || document.documentElement.dataset.country
      || 'PL';
  }

  // ── sessionStorage key — per product, includes season to avoid stale cross-season cache
  // productId is included so different products never share the same cache entry.

  function getSessionKey(productId, customerId) {
    var season = getSeason();
    if (customerId) {
      return 'naoko_rec_personal_' + customerId + '_' + productId + '_' + season;
    }
    return 'naoko_rec_' + productId + '_' + season;
  }

  // ── Request payload builder ────────────────────────────────────────────

  function buildRequest(productId) {
    return {
      customer_id:  getCustomerId(),
      product_id:   productId,
      order_count:  getOrderCount(),
      intent: (function() {
        var i = window.naokoIntent ? window.naokoIntent.getIntent() : null;
        return {
          color:      (i && i.color)      || null,
          size:       (i && i.size)       || null,
          style:      (i && i.style)      || null,
          confidence: (i && i.confidence) || 0
        };
      })(),
      context: {
        country:      getCountry(),
        season:       getSeason(),
        language:     getLanguage(),
        utm_campaign: new URLSearchParams(location.search).get('utm_campaign')
      }
    };
  }

  // ── Blank-slide helper — resets a cloned .product node to empty state ────
  // Used before addSlide so the clone carries no stale content.
  // swapWidget fills it immediately after.

  function clearSlideClone(clone) {
    var card = clone.querySelector('[data-ai-card]');
    if (!card) return;
    card.dataset.aiProductId = '';
    var img = card.querySelector('[data-ai-image]');
    if (img) img.src = 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==';
    var img2 = card.querySelector('[data-ai-image2]');
    if (img2) img2.src = 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==';
    var title = card.querySelector('[data-ai-title]');
    if (title) title.textContent = '';
    var price = card.querySelector('[data-ai-price]');
    if (price) price.textContent = '';
    card.querySelectorAll('[data-ai-url]').forEach(function (el) {
      el.href = '#';
      el.setAttribute('tabindex', '-1');
      el.setAttribute('aria-hidden', 'true');
    });
  }

  // ── applyRecommendations — single entry point for both Layer 2 and 3 ───
  // Order matters:
  //   1. Add missing slide wrappers to DOM (so swapWidget can find all N cards)
  //   2. swapWidget fills every card including newly added ones
  //   3. Slick refresh recalculates positions
  //
  // Slide insertion:
  //   - Slick initialized → addSlide (Slick manages the DOM)
  //   - Slick not yet ready (Layer 2 early cache hit) → appendChild (Slick
  //     picks them up when it initialises on DOMContentLoaded)

  function applyRecommendations(widgetEl, data) {
    var block    = document.getElementById('ai-recs-block-' + widgetEl.dataset.productId);
    var carousel = block ? block.querySelector('.products-carousel') : null;

    if (carousel) {
      var currentCount = carousel.querySelectorAll('.product').length;
      var neededCount  = data.products.length;

      if (neededCount > currentCount) {
        var firstSlide  = carousel.querySelector('.product');
        var $carousel   = window.jQuery ? window.jQuery(carousel) : null;
        var slickReady  = $carousel && $carousel.hasClass('slick-initialized');

        for (var s = currentCount; s < neededCount; s++) {
          var clone = firstSlide.cloneNode(true);
          clearSlideClone(clone);
          if (slickReady) {
            $carousel.slick('addSlide', clone.outerHTML);
          } else {
            carousel.appendChild(clone);
          }
        }
      }
    }

    // Fill ALL cards (original + newly added) in one pass
    swapWidget(widgetEl, data);

    // Refresh Slick positions after any structural change
    if (carousel && window.jQuery) {
      var $c = window.jQuery(carousel);
      if ($c.hasClass('slick-initialized')) {
        $c.slick('refresh');
      }
    }
  }

  // ── Widget swap — CLS = 0 ─────────────────────────────────────────────
  // Replaces ONLY content inside existing data-ai-* elements.
  // Never modifies innerHTML of the .ai-recs wrapper.
  // Identical DOM structure is required — maintained by the Liquid snippet.

  function swapWidget(widgetEl, data) {
    if (!data || !data.products || !data.products.length) return;

    var cards = widgetEl.querySelectorAll('[data-ai-card]');

    data.products.forEach(function (product, i) {
      var card = cards[i];
      if (!card) return;

      // Store numeric product ID on card for accurate tracking
      // Avoids parsing handle from URL which is unreliable
      card.dataset.aiProductId = product.id || '';

      // URLs — both link elements in the card share the same href
      card.querySelectorAll('[data-ai-url]').forEach(function (el) {
        el.href = product.url || '#';
        el.removeAttribute('tabindex');
        el.removeAttribute('aria-hidden');
      });

      // Image — also remove animated-loading so opacity:0 shimmer is cleared
      var img = card.querySelector('[data-ai-image]');
      var mediaEl = card.querySelector('.card-media');
      if (mediaEl) mediaEl.classList.remove('animated-loading');
      if (img && product.image) {
        img.src = product.image;
        img.alt = product.title || '';
      }

      // Hover image (second product photo) — L2/L3 path
      // Uses plain src (no srcset) — acceptable tradeoff for hover-only secondary image.
      // L1 (Liquid) uses lazyload snippet with full srcset for metafield-rendered cards.
      // media--hover-effect class is added/removed to activate ella6's native CSS transition.
      var img2 = card.querySelector('[data-ai-image2]');
      if (img2) {
        var supportsHover = window.matchMedia && window.matchMedia('(hover: hover)').matches;
        if (product.image2) {
          img2.src = product.image2;
          if (mediaEl) {
            if (supportsHover) {
              mediaEl.classList.add('media--hover-effect');
            } else {
              mediaEl.classList.remove('media--hover-effect');
            }
          }
        } else {
          // No second image — remove hover effect class so cursor doesn't trigger empty swap
          if (mediaEl) mediaEl.classList.remove('media--hover-effect');
        }
      }

      // Title
      var title = card.querySelector('[data-ai-title]');
      if (title) title.textContent = product.title || '';

      // Price — render sale price with optional strikethrough compare price.
      // product.price          = current sale/regular price (decimal string "111.00")
      // product.compareAtPrice = original price when on sale, null otherwise
      // Uses Shopify.formatMoney with window.money_format (theme-specific global).
      // moneyFmt / isOnSale declared here (var = function-scoped) so omnibus block can read them.
      var moneyFmt = window.money_format
        || (window.Shopify && window.Shopify.money_format)
        || '{{amount}}';
      var fmt = (window.Shopify && window.Shopify.formatMoney)
        ? function(amount) { return window.Shopify.formatMoney(Math.round(parseFloat(amount) * 100), moneyFmt); }
        : function(amount) { return amount; };
      var isOnSale = !!(product.compareAtPrice
        && parseFloat(product.compareAtPrice) > parseFloat(product.price));

      var priceEl = card.querySelector('[data-ai-price]');
      if (priceEl && product.price) {
        if (isOnSale) {
          var pct = Math.round((parseFloat(product.compareAtPrice) - parseFloat(product.price))
            / parseFloat(product.compareAtPrice) * 100);
          priceEl.innerHTML =
            '<span class="price-item price-item--sale" style="color:#ED4D10;font-weight:600;">' + fmt(product.price) + '</span>' +
            ' <s class="price-item price-item--regular" style="color:#888;font-size:0.85em;">' + fmt(product.compareAtPrice) + '</s>' +
            ' <span class="badge sale-badge" style="background:#ED4D10;color:#fff;padding:1px 5px;font-size:12px;border-radius:3px;font-weight:700;">-' + pct + '%</span>';
        } else {
          priceEl.textContent = fmt(product.price);
        }
      }

      // AI reason — max 10 words, in user's language (set by backend)
      var reason = card.querySelector('[data-ai-reason]');
      if (reason) reason.textContent = product.reason || '';

      // Omnibus EU price — "Najniższa cena z 30 dni: X"
      // Only shown when product is on sale AND Complimus label is not "Not compliant"
      var omnibusEl = card.querySelector('[data-ai-omnibus]');
      if (omnibusEl) {
        if (product.omnibusPrice && isOnSale) {
          omnibusEl.textContent = 'Najniższa cena z 30 dni: ' + fmt(product.omnibusPrice);
          omnibusEl.style.display = '';
        } else {
          omnibusEl.textContent = '';
          omnibusEl.style.display = 'none';
        }
      }

      // Discount box — uses discount code from current product's collection,
      // discount amount computed from recommended product's price.
      var discountEl = card.querySelector('[data-ai-discount]');
      if (discountEl) {
        var nd = window.__naokoDiscount;
        if (nd && nd.code && nd.pct && nd.msg && product.price) {
          var dAmount = fmt(parseFloat(product.price) * nd.pct / 100);
          var dHtml = nd.msg
            .replace(/discount_code/g, nd.code)
            .replace(/discount_amount/g, dAmount)
            .replace(/discount_value/g, String(nd.pct));
          discountEl.innerHTML =
            '<div class="discount-info__box" style="cursor:pointer;">' +
            '<div class="discount__text">' + dHtml + '</div>' +
            '<i class="copy-icon"></i>' +
            '</div>';
          discountEl.style.display = '';
          discountEl.onclick = (function (code) {
            return function () {
              if (navigator.clipboard) navigator.clipboard.writeText(code);
            };
          })(nd.code);
        } else {
          discountEl.style.display = 'none';
          discountEl.innerHTML = '';
        }
      }

      // Size buttons — one <li> per variant matching theme's product-item-size.liquid structure
      // Overlay sits on the image; variant-disabled = OOS (line-through, grey)
      var sizesEl = card.querySelector('[data-ai-sizes]');
      if (sizesEl && product.variants && product.variants.length > 0) {
        sizesEl.innerHTML = '<ul class="sizes-list">'
          + product.variants.map(function(v) {
              return '<li class="size-item">'
                + '<a href="' + v.url + '"'
                + (v.availableForSale ? '' : ' class="variant-disabled" aria-label="' + v.title + ' — wyprzedane"')
                + '>' + v.title + '</a>'
                + '</li>';
            }).join('')
          + '</ul>';
        sizesEl.style.display = '';
      } else if (sizesEl) {
        sizesEl.style.display = 'none';
      }

    });

    widgetEl.dataset.state = 'personalized';
  }

  // ── Click tracking (sendBeacon) ────────────────────────────────────────
  // Non-blocking — survives page navigation.
  // POST /api/track with variantId, position, productId, sourceProductId.

  function setupTracking(widgetEl, sourceProductId, apiUrl) {
    widgetEl.addEventListener('click', function (e) {
      var card = e.target.closest('[data-ai-card]');
      if (!card) return;

      var cards    = Array.from(widgetEl.querySelectorAll('[data-ai-card]'));
      var position = cards.indexOf(card) + 1;
      if (!position) return;

      // Use numeric product ID stored by swapWidget — never parse from URL handle
      var recProductId = card.dataset.aiProductId || '';

      // variantId set by swapWidget (Layer 3) or by Liquid from metafield (Layer 1)
      var variantId = widgetEl.dataset.variantId || 'unknown';

      var payload = JSON.stringify({
        variantId:       variantId,
        productId:       recProductId,
        position:        position,
        sourceProductId: sourceProductId,
        customerId:      getCustomerId()
      });

      if (navigator.sendBeacon) {
        navigator.sendBeacon(
          apiUrl + '/api/track',
          new Blob([payload], { type: 'application/json' })
        );
      }
    });
  }

  // ── Main widget init ───────────────────────────────────────────────────

  function initWidget(widgetEl) {
    var productId   = widgetEl.dataset.productId;
    var productType = widgetEl.dataset.productType;

    if (!productId) return;

    var apiUrl     = getApiUrl(widgetEl);
    var customerId = getCustomerId();
    var sessionKey = getSessionKey(productId, customerId);

    setupTracking(widgetEl, productId, apiUrl);

    // ── Product color from metafield (023_kolor_wiodacy) ───────────────
    // Liquid injects the dominant color into data-ai-color at render time.
    // We register it as a color signal immediately — no click required.
    // This gives archetype-detector a head start before the user interacts.
    var productColor = widgetEl.dataset.aiColor;
    if (productColor && window.naokoIntent) {
      window.naokoIntent.addSignal('color', productColor);
    }

    // ── Layer 2: sessionStorage cache ──────────────────────────────────
    try {
      var cached = sessionStorage.getItem(sessionKey);
      if (cached) {
        var data = JSON.parse(cached);
        // Restore variantId from cache so tracking works correctly
        if (data.variantId) widgetEl.dataset.variantId = data.variantId;
        applyRecommendations(widgetEl, data);
        return; // cache hit — done
      }
    } catch (e) {
      // sessionStorage unavailable or JSON parse error — fall through to Layer 3
    }

    // ── Layer 3: API fetch ─────────────────────────────────────────────

    var request = buildRequest(productId);

    fetch(apiUrl + '/recommend', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify(request)
    })
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.json();
      })
      .then(function (data) {
        if (!data || !data.products || !data.products.length) return;

        // Store variantId on element for tracking
        if (data.variantId) widgetEl.dataset.variantId = data.variantId;

        // Cache in sessionStorage — avoids re-fetching on next PDP view this session
        try {
          sessionStorage.setItem(sessionKey, JSON.stringify(data));
        } catch (e) {
          // sessionStorage full or unavailable — still swap the widget
        }

        applyRecommendations(widgetEl, data);
      })
      .catch(function (err) {
        console.warn('[ai-recs] fetch failed:', err.message);
        // state=ready: L1 products already visible — nothing to do.
        // state=loading: no L1 data and API failed — hide widget entirely.
        if (widgetEl.dataset.state === 'loading') {
          widgetEl.style.display = 'none';
        }
      });
  }

  // ── Boot ───────────────────────────────────────────────────────────────

  function init() {
    document.querySelectorAll('[id^="ai-recs-"]').forEach(initWidget);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
